import { LanguageCode } from 'i18n/internationalization.types';
import {
  CustomTemplateWithLocales,
  MessagingType,
} from 'models/automation/CustomTemplates';
import * as Yup from 'yup';
import { OptionalObjectSchema } from 'yup/lib/object';

const perLanguageSchema = ({
  messagingType,
  requireFields = true,
}: {
  messagingType: MessagingType;
  requireFields?: boolean;
}) => {
  const contentSchema = requireFields
    ? Yup.string().richTextEditorContentRequired()
    : Yup.string().optional().nullable();

  const contentHtmlContentSchema = requireFields
    ? Yup.string().required()
    : Yup.string().optional().nullable();

  const subjectSchema = Yup.string().when({
    is: () => messagingType === MessagingType.EMAIL && requireFields,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional().nullable(),
  });

  return Yup.object({
    content: contentSchema,
    content_htmlContent: contentHtmlContentSchema,
    subject: subjectSchema,
  }).nullable();
};

const localesSchema = ({
  availableLanguages,
  messagingType,
  autoTranslate,
}: {
  availableLanguages: LanguageCode[];
  messagingType: MessagingType;
  autoTranslate: boolean;
}) =>
  Yup.object({
    ...availableLanguages.reduce(
      (acc, language) => {
        const requireFields = language === LanguageCode.EN || !autoTranslate;
        return {
          ...acc,
          [language]: requireFields
            ? perLanguageSchema({
                messagingType,
                requireFields: true,
              }).required()
            : perLanguageSchema({
                messagingType,
                requireFields: false,
              }).optional(),
        };
      },
      {} as {
        [key in LanguageCode]: ReturnType<typeof perLanguageSchema>;
      },
    ),
  });

export const customTemplateAddEditFormSchema = ({
  availableLanguages,
  messagingType,
}: {
  availableLanguages: LanguageCode[];
  messagingType: MessagingType;
}) => ({
  uid: Yup.string().nullable().optional(),
  name: Yup.string().required(),
  messagingType: Yup.mixed<MessagingType>()
    .oneOf(Object.values(MessagingType))
    .required(),
  language: Yup.string().required(),
  autoTranslate: Yup.boolean().optional().nullable(),
  locales: Yup.object().when('autoTranslate', {
    is: true,
    then: localesSchema({
      availableLanguages,
      messagingType,
      autoTranslate: true,
    }),
    otherwise: localesSchema({
      availableLanguages,
      messagingType,
      autoTranslate: false,
    }),
  }) as ReturnType<typeof localesSchema>,
});

export type CustomTemplateAddEditFormValues = Yup.InferType<
  OptionalObjectSchema<ReturnType<typeof customTemplateAddEditFormSchema>>
>;

export const getFormDefaultValues = ({
  customTemplate,
  templateType,
  availableLanguages,
  currentLanguage,
  autoTranslate = false,
}: {
  customTemplate?: CustomTemplateWithLocales;
  templateType?: MessagingType;
  availableLanguages: LanguageCode[];
  currentLanguage: LanguageCode;
  autoTranslate?: boolean;
}): CustomTemplateAddEditFormValues => {
  return {
    uid: customTemplate?.uid ?? null,
    name: customTemplate?.name ?? '',
    // @ts-expect-error we will always have either the template or just templateType
    messagingType: customTemplate?.messagingType ?? templateType,
    language: currentLanguage,
    autoTranslate,
    locales: availableLanguages.reduce((acc, language) => {
      const localeContents = customTemplate?.locales[language];

      return {
        ...acc,
        [language]: {
          content: localeContents?.content ?? '<p></p>',
          content_htmlContent: localeContents?.content ?? '<p></p>',
          subject: localeContents?.subject ?? '',
        },
      };
    }, {} as CustomTemplateAddEditFormValues['locales']),
  };
};
